import {useCallback, useMemo, useRef, useState} from "react";
import {DiagramConnectionId, DiagramId, DiagramNodeId, UpdateDiagram} from "platformed-client/types";
import {usePromiseState} from "../../../../../../../hooks/promiseState";
import {ClientDiagramConnection, ClientDiagramNode} from "./types";
import {unreachableCase} from "../../../../../../../utils/typescript";
import api from "../../../../../../../api";

const SYNC_DEBOUNCE_MS = 1000;

type UpdatePayload<T> = {type: "Upsert"; object: T} | {type: "Delete"};

type PendingUpdates = {
  nodes: Map<DiagramNodeId, UpdatePayload<ClientDiagramNode>>;
  connections: Map<DiagramConnectionId, UpdatePayload<ClientDiagramConnection>>;
};

function emptyPendingUpdates(): PendingUpdates {
  return {nodes: new Map(), connections: new Map()};
}

function isPendingUpdatesEmpty(pendingUpdates: PendingUpdates): boolean {
  return pendingUpdates.nodes.size === 0 && pendingUpdates.connections.size === 0;
}

function constructServerUpdate(pendingUpdates: PendingUpdates): UpdateDiagram {
  const update: UpdateDiagram = {
    upsert_nodes: [],
    delete_nodes: [],
    upsert_connections: [],
    delete_connections: [],
    upsert_lanes: [],
    delete_lanes: [],
  };

  for (const [nodeId, nodeUpdate] of pendingUpdates.nodes.entries()) {
    // TODO(WF): these could be from/to functions
    switch (nodeUpdate.type) {
      case "Upsert":
        {
          const node = nodeUpdate.object;

          update.upsert_nodes.push({
            diagram_node_id: node.id,
            label: node.label,
            description: node.description,
            icon: node.icon,
            position: node.position.tuple(),
          });
        }
        break;

      case "Delete":
        {
          update.delete_nodes.push(nodeId);
        }
        break;

      default:
        unreachableCase(nodeUpdate);
    }
  }

  for (const [connectionId, connectionUpdate] of pendingUpdates.connections.entries()) {
    switch (connectionUpdate.type) {
      case "Upsert":
        {
          const connection = connectionUpdate.object;

          update.upsert_connections.push({
            diagram_connection_id: connection.id,
            source_node_id: connection.from,
            target_node_id: connection.to,
            label: connection.label,
            color: connection.color,
            anchors: connection.anchors.map(a => ({position: a.position.tuple()})),
          });
        }
        break;

      case "Delete":
        {
          update.delete_connections.push(connectionId);
        }
        break;

      default:
        unreachableCase(connectionUpdate);
    }
  }

  return update;
}

export function useBackendSync(diagramId: DiagramId) {
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingUpdates = useRef<PendingUpdates>(emptyPendingUpdates());
  const [timeoutPending, setTimeoutPending] = useState<boolean>(false);

  const [saving, save] = usePromiseState(async () => {
    const toSync = pendingUpdates.current;
    pendingUpdates.current = emptyPendingUpdates();

    const payload = constructServerUpdate(toSync);
    await api.tprm.diagrams.update(diagramId, payload);

    timeout.current = isPendingUpdatesEmpty(pendingUpdates.current) ? null : setTimeout(save, SYNC_DEBOUNCE_MS);
    setTimeoutPending(timeout.current != null);
  }, [diagramId]);

  const enqueueSync = useCallback(() => {
    if (saving.inProgress) {
      return;
    }

    if (timeout.current != null) {
      clearTimeout(timeout.current);
    }

    timeout.current = isPendingUpdatesEmpty(pendingUpdates.current) ? null : setTimeout(save, SYNC_DEBOUNCE_MS);
    setTimeoutPending(timeout.current != null);
  }, [save, saving.inProgress]);

  const upsertNode = useCallback(
    (node: ClientDiagramNode) => {
      pendingUpdates.current.nodes.set(node.id, {type: "Upsert", object: node});
      enqueueSync();
    },
    [enqueueSync],
  );

  const deleteNode = useCallback(
    (nodeId: DiagramNodeId) => {
      pendingUpdates.current.nodes.set(nodeId, {type: "Delete"});
      enqueueSync();
    },
    [enqueueSync],
  );

  const upsertConnection = useCallback(
    (connection: ClientDiagramConnection) => {
      pendingUpdates.current.connections.set(connection.id, {type: "Upsert", object: connection});
      enqueueSync();
    },
    [enqueueSync],
  );

  const deleteConnection = useCallback(
    (connectionId: DiagramConnectionId) => {
      pendingUpdates.current.connections.set(connectionId, {type: "Delete"});
      enqueueSync();
    },
    [enqueueSync],
  );

  return useMemo(
    () => ({upsertNode, deleteNode, upsertConnection, deleteConnection, saving: timeoutPending}),
    [upsertNode, deleteNode, upsertConnection, deleteConnection, timeoutPending],
  );
}
